import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import {
  ActionBlockModal,
  ActionRetryModal,
  CIDocForm,
  QuestionsChallengeForm,
  VerifyClaveUnicaLogin,
} from "./components";
import { Modal } from "@components/Modal";
import {
  CIDocFormData,
  QuestionsChallengeActionError,
  Step,
  ValidationChallengePayload,
  ValidationChallengeResponse,
} from "@interfaces";
import { apiGateway, useDoctorContext } from "@contexts";
import {
  ErrorType,
  onQuestionChallengeAxiosErrorInterpreter,
} from "./hooks/onQuestionChallengeError";
import {
  QUESTIONS_CHALLENGE_ERROR_MESSAGES,
} from "./constants/questionsChallengeErrorMessages";

const MAX_RETRY_MODAL_OPEN = 2;

export const ValidationChallengeScreen: React.FC = () => {
  const doctor = useDoctorContext();

  const [step, setStep] = useState<Step>("validatingClaveUnicaLogin");
  const [showValidateDoctorDataModal, setShowValidateDoctorDataModal] =
    useState(false);
  const [showValidateDoctorDataInputs, setShowValidateDoctorDataInputs] =
    useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockModalContent, setBlockModalContent] = useState<{
    title: string;
    description?: string | React.ReactNode;
  }>({ title: "" });
  const [showRetryModal, setShowRetryModal] = useState(false);
  const [retryModalOpenCount, setRetryModalOpenCount] = useState(0);
  const [retryModalContent, setRetryModalContent] = useState<{
    title: string;
    description?: string | React.ReactNode;
  }>({ title: "" });
  const [docNumErrorDescription, setDocNumErrorDescription] = useState<
    string | React.ReactNode | null
  >(null);

  const form = useForm<CIDocFormData>({
    defaultValues: {
      docNum: "",
      name: doctor.name,
      firstSurname: doctor.lastName,
      secondSurname: "",
      birthdate: doctor.birthdate,
    },
  });

  const {
    mutate: createValidationChallenge,
    isPending: isCreatingValidationChallenge,
    data: questions,
    variables: validationChallengePayload,
  } = useMutation({
    mutationFn: async (
      validationChallengePayload: ValidationChallengePayload,
    ) => {
      const { questions } = await apiGateway<
        ValidationChallengeResponse,
        ErrorType
      >(
        {
          method: "post",
          url: "/enrolment/questions-challenge",
          data: validationChallengePayload,
        },
        onQuestionChallengeAxiosErrorInterpreter,
      );

      return questions;
    },
    onSuccess: () => {
      setDocNumErrorDescription(null);
      if (step === "ciDocForm") {
        setStep("questionsForm");
      }
    },
    onError: (error) => {
      if (error instanceof QuestionsChallengeActionError) {
        switch (error.action) {
          case "block":
            setBlockModalContent({
              title: error.name,
              description: error.description,
            });
            setShowBlockModal(true);
            return;
          case "invalidInput":
            form.setError("docNum", {
              type: "manual",
              message: error.name,
            });
            setDocNumErrorDescription(error.description ?? null);
            return;
          case "retry": {
            const nextCount = retryModalOpenCount + 1;
            setRetryModalOpenCount(nextCount);
            if (nextCount <= MAX_RETRY_MODAL_OPEN) {
              setRetryModalContent({
                title: error.name,
                description: error.description,
              });
              setShowRetryModal(true);
            } else {
              const exceeded = QUESTIONS_CHALLENGE_ERROR_MESSAGES.exceededMaxRetries;
              setBlockModalContent({
                title: exceeded.name,
                description: exceeded.description,
              });
              setShowBlockModal(true);
            }
            return;
          }
          case "incompleteData":
            setShowValidateDoctorDataModal(true);
            setShowValidateDoctorDataInputs(true);
            return;
        }
      }
      const frontErrorMessage =
        error.message ?? "Ha ocurrido un error, por favor intenta nuevamente.";
      setDocNumErrorDescription(null);
      form.setError("docNum", {
        type: "manual",
        message: frontErrorMessage,
      });
    },
  });

  if (step === "validatingClaveUnicaLogin")
    return <VerifyClaveUnicaLogin setStep={setStep} />;
  if (step === "ciDocForm")
    return (
      <>
        <CIDocForm
          form={form}
          showValidateDoctorDataInputs={showValidateDoctorDataInputs}
          createValidationChallenge={createValidationChallenge}
          isCreatingValidationChallenge={isCreatingValidationChallenge}
          docNumErrorDescription={docNumErrorDescription}
        />
        <ActionBlockModal
          isOpen={showBlockModal}
          onClose={() => setShowBlockModal(false)}
          title={blockModalContent.title}
          description={blockModalContent.description}
        />
        <ActionRetryModal
          isOpen={showRetryModal}
          onClose={() => setShowRetryModal(false)}
          title={retryModalContent.title}
          description={retryModalContent.description}
        />
        <Modal
          title="Se requiere información adicional"
          content="Algunos datos del Registro Nacional de Prestadores Individuales de Salud no fueron encontrados. Por favor, completa los nuevos campos requeridos."
          isOpen={showValidateDoctorDataModal}
          onClose={() => setShowValidateDoctorDataModal(false)}
          buttonProps={{
            buttonText: "Aceptar",
            onClick: () => setShowValidateDoctorDataModal(false),
          }}
        />
      </>
    );
  if (validationChallengePayload && step === "questionsForm") {
    return (
      <QuestionsChallengeForm
        questions={questions}
        setStep={setStep}
        createValidationChallenge={createValidationChallenge}
        isCreatingValidationChallenge={isCreatingValidationChallenge}
        validationChallengePayload={validationChallengePayload}
      />
    );
  }
  return null;
};
